import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchSafetyPlanUrdu } from "../../../store/slices/safetyPlansUrduSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function SafetyPlanUrduFormPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { currentSafetyPlanUrdu, isLoading, error } = useSelector((state) => state.safetyPlansUrdu || {});

    useEffect(() => {
        if (id) dispatch(fetchSafetyPlanUrdu(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (currentSafetyPlanUrdu) {
            const timer = setTimeout(() => window.print(), 1000);
            window.onafterprint = () => navigate(-1);
            return () => {
                clearTimeout(timer);
                window.onafterprint = null;
            };
        }
    }, [currentSafetyPlanUrdu, navigate]);

    if (isLoading) return <div className="p-8 text-center">Loading Safety Plan (Urdu)...</div>;
    if (error) return <div className="p-8 text-danger text-center">{error}</div>;
    if (!currentSafetyPlanUrdu) return <div className="p-8 text-center">No record found.</div>;

    const d = currentSafetyPlanUrdu;

    // Sections mapping to match backend keys and Urdu titles
    const sections = [
        { id: "warning_signs", title: "۱. انتباہی علامات (خیالات، تصاویر، موڈ، صورتحال، رویہ) جو بحران کی نشاندہی کرتی ہیں:" },
        { id: "internal_coping", title: "۲. اندرونی مقابلہ کی حکمت عملیاں – وہ چیزیں جو میں دوسرے شخص سے رابطہ کیے بغیر مسائل سے ذہن ہٹانے کے لیے کر سکتا ہوں:" },
        { id: "social_distractions", title: "۳. لوگ اور سماجی ماحول جو توجہ ہٹانے میں مدد دیتے ہیں:" },
        { id: "people_for_help", title: "۴. وہ لوگ جن سے میں مدد مانگ سکتا ہوں:" },
        { id: "professionals", title: "۵. پیشہ ور افراد یا ایجنسیاں جن سے بحران کے دوران رابطہ کیا جا سکتا ہے:" },
        { id: "safe_environment", title: "۶. ماحول کو محفوظ بنانا:" },
        { id: "reasons_for_living", title: "۷. زندگی کی وجوہات – وہ ایک چیز جو میرے لیے سب سے اہم ہے اور زندہ رہنے کے قابل ہے:" },
        { id: "additional_notes", title: "۸. اضافی نوٹس / دیگر حکمت عملیاں:" }
    ];

    return (
        <>
            <style jsx global>{`
                * { box-sizing: border-box; }
                body { background: #fff !important; color: #000 !important; font-family: 'Jameel Noori Nastaleeq', 'Urdu Typesetting', Arial, sans-serif !important; font-size: 14px !important; line-height: 1.8 !important; margin: 0 !important; padding: 0 !important; }
                .print-container { padding: 40px !important; position: relative !important; direction: rtl !important; text-align: right !important; }
                ${printFooterStyles}
                
                .logo { position: absolute !important; top: 20px !important; right: 20px !important; }
                .logo img { height: 70px !important; width: auto !important; }
                
                .header { text-align: center !important; margin-bottom: 30px !important; border-bottom: 2px solid #000 !important; padding-bottom: 10px !important; width: 100% !important; }
                .header h3 { font-size: 2rem !important; font-weight: bold !important; margin-bottom: 5px !important; }
                .header h6 { font-size: 1.4rem !important; text-decoration: underline !important; }

                .meta-section { margin-bottom: 20px !important; display: flex !important; justify-content: space-between !important; border: 1px solid #000 !important; padding: 10px !important; background: #f9f9f9 !important; }
                
                .section-box { margin-bottom: 20px !important; border: 1px solid #000 !important; page-break-inside: avoid !important; }
                .section-title { background: #eee !important; padding: 8px 15px !important; font-weight: bold !important; border-bottom: 1px solid #000 !important; font-size: 1.1rem !important; text-align: right !important; }
                .section-content { padding: 15px !important; min-height: 80px !important; white-space: pre-wrap !important; font-size: 1.1rem !important; text-align: right !important; }

                .sig-row { display: flex !important; justify-content: space-between !important; margin-top: 60px !important; }
                .sig-box { border-top: 1.5px solid #000 !important; width: 250px !important; text-align: center !important; padding-top: 10px !important; font-weight: bold !important; font-size: 1.2rem !important; }

                .footer { margin-top: 40px !important; border-top: 1px solid #000 !important; padding-top: 10px !important; text-align: center !important; font-size: 12px !important; direction: ltr !important; }
            `}</style>

            <PrintWrapper>
                <div className="print-container">
                <div className="logo"><img src={logo} alt="Logo" /></div>
                <div className="header">
                    <h3>دی ریکوری ہاؤس</h3>
                    <h6>حفاظتی منصوبہ (Safety Plan)</h6>
                </div>

                <div className="meta-section">
                    <div>کلائنٹ کا نام: <strong>{d.client_name}</strong></div>
                    <div>تاریخ: <strong>{new Date(d.created_at).toLocaleDateString('ur-PK')}</strong></div>
                </div>

                {sections.map((section) => (
                    <div className="section-box" key={section.id}>
                        <div className="section-title">{section.title}</div>
                        <div className="section-content">
                            {d[section.id] || "کوئی تفصیل درج نہیں کی گئی۔"}
                        </div>
                    </div>
                ))}

                <div className="sig-row">
                    <div className="sig-box">کلائنٹ کے دستخط</div>
                    <div className="sig-box">پیشہ ورانہ ماہر کے دستخط</div>
                </div>

                <div className="footer">
                    <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan | Ph: 021-34546364-65-66</p>
                    <p><strong>A Project of “CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
                </div>
            </div>
            </PrintWrapper>
        </>
    );
}